import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { listCrews, createCrew, deleteCrew, addCrewMember, removeCrewMember, notifyCrewAboutShift, type Crew } from "@/lib/crews.functions";
import { PostCrewShiftDialog } from "@/components/PostCrewShiftDialog";
import { getFavoriteBartenders } from "@/lib/favorites.functions";
import { supabase } from "@/integrations/supabase/client";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { haptic } from "@/lib/haptics";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/venue/crews")({
  head: () => ({
    meta: [
      { title: "Saved Crews — The Hospitality Reserve" },
      { name: "description", content: "Group your favourite bartenders into reusable crews and invite them in one tap." },
    ],
  }),
  component: VenueCrewsPage,
});

function VenueCrewsPage() {
  const list = useServerFn(listCrews);
  const create = useServerFn(createCrew);
  const del = useServerFn(deleteCrew);
  const add = useServerFn(addCrewMember);
  const remove = useServerFn(removeCrewMember);
  const favs = useServerFn(getFavoriteBartenders);
  const notify = useServerFn(notifyCrewAboutShift);

  const [crews, setCrews] = useState<Crew[]>([]);
  const [favorites, setFavorites] = useState<{ id: string; full_name: string | null }[]>([]);
  const [openShifts, setOpenShifts] = useState<{ id: string; role_type: string; start_time: string }[]>([]);
  const [selectedShift, setSelectedShift] = useState<Record<string, string>>({});
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);
  const [postCrew, setPostCrew] = useState<Crew | null>(null);

  async function refresh() {
    const [c, f] = await Promise.all([list({ data: undefined }), favs({ data: undefined })]);
    setCrews(c);
    setFavorites(f.favorites.map((p) => ({ id: p.id, full_name: p.full_name })));
    const { data } = await supabase
      .from("shifts")
      .select("id,role_type,start_time")
      .eq("status", "open")
      .gt("start_time", new Date().toISOString())
      .order("start_time", { ascending: true })
      .limit(20);
    setOpenShifts((data ?? []) as { id: string; role_type: string; start_time: string }[]);
    setLoading(false);
  }
  useEffect(() => { refresh(); }, []);

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <main className="max-w-4xl mx-auto px-4 py-10 space-y-8">
        <header>
          <h1 className="text-3xl font-semibold">Saved crews</h1>
          <p className="text-muted-foreground mt-1">Group bartenders you trust and invite the whole crew at once.</p>
        </header>

        <section className="border rounded-xl p-4 bg-card space-y-3">
          <h2 className="font-medium">New crew</h2>
          <div className="flex gap-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Weekend A-team" maxLength={60} />
            <Button
              disabled={!name.trim()}
              onClick={async () => {
                await create({ data: { name: name.trim() } });
                haptic("success");
                setName("");
                refresh();
              }}
            >Create</Button>
          </div>
        </section>

        {loading ? <p className="text-muted-foreground">Loading…</p> : crews.length === 0 ? (
          <p className="text-muted-foreground">No crews yet — favourite some bartenders from <Link to="/favorites" className="underline">My favourites</Link>, then group them here.</p>
        ) : (
          <div className="space-y-4">
            {crews.map((c) => {
              const memberIds = new Set(c.members.map((m) => m.bartender_id));
              const available = favorites.filter((f) => !memberIds.has(f.id));
              return (
                <div key={c.id} className="border rounded-xl p-4 bg-card space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium">{c.name}</h3>
                    <Button variant="ghost" size="sm" onClick={async () => {
                      if (!confirm(`Delete crew "${c.name}"?`)) return;
                      await del({ data: { id: c.id } });
                      haptic("warn");
                      refresh();
                    }}>Delete</Button>
                  </div>
                  <ul className="space-y-1">
                    {c.members.length === 0 && <li className="text-sm text-muted-foreground">No members yet.</li>}
                    {c.members.map((m) => (
                      <li key={m.bartender_id} className="flex items-center justify-between text-sm">
                        <span>{m.full_name ?? "Bartender"}</span>
                        <button className="text-xs underline text-muted-foreground" onClick={async () => {
                          await remove({ data: { crew_id: c.id, bartender_id: m.bartender_id } });
                          refresh();
                        }}>Remove</button>
                      </li>
                    ))}
                  </ul>
                  {available.length > 0 && (
                    <details className="text-sm">
                      <summary className="cursor-pointer text-primary">Add from favourites</summary>
                      <ul className="pt-2 space-y-1">
                        {available.map((f) => (
                          <li key={f.id} className="flex items-center justify-between">
                            <span>{f.full_name ?? "Bartender"}</span>
                            <Button size="sm" variant="outline" onClick={async () => {
                              await add({ data: { crew_id: c.id, bartender_id: f.id } });
                              haptic("tap");
                              refresh();
                            }}>Add</Button>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                  {openShifts.length > 0 ? (
                    <div className="flex flex-wrap gap-2 items-center pt-2 border-t">
                      <select
                        className="text-sm border rounded-md px-2 py-1 bg-background"
                        value={selectedShift[c.id] ?? ""}
                        onChange={(e) => setSelectedShift((s) => ({ ...s, [c.id]: e.target.value }))}
                      >
                        <option value="">Select an open shift…</option>
                        {openShifts.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.role_type} · {new Date(s.start_time).toLocaleString("en-GB")}
                          </option>
                        ))}
                      </select>
                      <Button size="sm"
                        disabled={!selectedShift[c.id] || c.members.length === 0}
                        onClick={async () => {
                          try {
                            const res = await notify({ data: { crew_id: c.id, shift_id: selectedShift[c.id] } });
                            haptic("success");
                            toast.success(`Invited ${res.notified} bartender${res.notified === 1 ? "" : "s"}`);
                          } catch (e: unknown) {
                            toast.error(e instanceof Error ? e.message : "Could not invite crew.");
                          }
                        }}
                      >Invite crew</Button>
                      <Button size="sm" variant="outline" disabled={c.members.length === 0} onClick={() => setPostCrew(c)}>
                        Post &amp; auto-fill
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2 items-center pt-2 border-t">
                      <p className="text-xs text-muted-foreground">
                        No open shifts to invite to yet.
                      </p>
                      <Button size="sm" variant="outline" disabled={c.members.length === 0} onClick={() => setPostCrew(c)}>
                        Post &amp; auto-fill crew shift
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
      {postCrew && (
        <PostCrewShiftDialog
          open={!!postCrew}
          onOpenChange={(v) => !v && setPostCrew(null)}
          crew={postCrew}
          onPosted={() => { setPostCrew(null); refresh(); }}
        />
      )}
    </div>
  );
}

// Surface toast import for side-effect free build environments.
void toast;
